/**
 * "Continue with Google".
 *
 * ═══ Google renders this button, we do not ════════════════════════════════════
 *
 * It would be easy to draw our own button and call `google.accounts.id.prompt()`. Google Identity
 * Services deliberately does not support that: the credential callback only fires for a button GIS
 * itself rendered, and its branding terms require the real thing. So this component owns a container
 * div and hands it to `renderButton`, which is why there is no JSX for the button face here.
 *
 * The consequence worth knowing: the button's width is set explicitly, because GIS sizes to its
 * `width` option and ignores CSS on the host element. 340 matches the 380px form column minus padding.
 *
 * ═══ One script, one initialisation ═══════════════════════════════════════════
 *
 * `google.accounts.id.initialize` is global and last-call-wins. Sign-in and sign-up both render this
 * component, and if both mounted at once the second `initialize` would silently repoint the callback —
 * so the two screens are never mounted together, and this component re-initialises on mount to make
 * sure the live callback is its own.
 *
 * ═══ The credential never decides anything here ═══════════════════════════════
 *
 * The callback receives a JWT and forwards it verbatim. It is not decoded, not inspected, and nothing
 * on screen is driven by its contents. Every claim in it is verified server-side against Google's
 * published keys — including the audience check that stops a token minted for another site being
 * replayed — and a client that read `email` out of it to render a greeting would be trusting an
 * unverified string. Given the token is the credential, that habit is worth not forming.
 */
import { useEffect, useRef, useState } from "react";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (opts: {
            client_id: string;
            callback: (res: { credential?: string }) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
            use_fedcm_for_prompt?: boolean;
          }) => void;
          renderButton: (el: HTMLElement, opts: Record<string, unknown>) => void;
        };
      };
    };
  }
}

const GIS_SRC = "https://accounts.google.com/gsi/client";

/** Load the GIS script once per document. */
function loadGis(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("gis failed to load")));
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("gis failed to load"));
    document.head.appendChild(script);
  });
}

export function GoogleButton({
  clientId,
  text,
  onCredential,
  disabled,
}: {
  clientId: string;
  /** GIS renders the wording itself; this picks which of its phrasings to use. */
  text: "signin_with" | "signup_with" | "continue_with";
  onCredential: (credential: string) => void;
  disabled?: boolean;
}) {
  const host = useRef<HTMLDivElement | null>(null);
  const [broken, setBroken] = useState(false);

  /*
   * The callback is held in a ref rather than closed over.
   *
   * GIS keeps whichever function it was initialised with, so a re-render that produced a new
   * `onCredential` would leave GIS calling the stale one — the classic version of this bug is a
   * "Continue with Google" that signs you in using the state from first paint. The ref means GIS always
   * reaches the current handler without needing to be re-initialised on every render.
   */
  const handler = useRef(onCredential);
  handler.current = onCredential;

  /**
   * The width GIS should render at.
   *
   * ═══ Why this is measured and not a constant ══════════════════════════════
   *
   * GIS takes `width` as a NUMBER OF PIXELS and ignores CSS on its host element — there is no
   * `width: 100%`. A hard-coded value is therefore wrong at every viewport except the one it was
   * chosen for, and the first version of this component used 340 against a 380px form column, which
   * left the Google button visibly narrower than every input beneath it. On a 390px phone the same
   * constant would have been too WIDE and overflowed.
   *
   * So the host element is measured and the button re-rendered when that width changes. Clamped to
   * GIS's own accepted range (200–400); a value outside it is rejected and the button does not render
   * at all.
   */
  const [width, setWidth] = useState<number | null>(null);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const measure = () => {
      const w = Math.round(el.getBoundingClientRect().width);
      if (w <= 0) return; // Not laid out yet, or hidden.
      setWidth(Math.max(200, Math.min(400, w)));
    };
    measure();
    // Covers an orientation change, a desktop window drag, and the rail collapsing at 900px.
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (width === null) return;
    let cancelled = false;
    void loadGis()
      .then(() => {
        if (cancelled || !host.current || !window.google?.accounts?.id) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (res) => {
            if (res?.credential) handler.current(res.credential);
          },
          // No One Tap and no automatic sign-in. This is an operational console: somebody opening it
          // should choose to sign in, not be signed in by a browser prompt they did not ask for.
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        /*
         * Cleared before re-rendering. `renderButton` APPENDS, so a width change would otherwise
         * stack a second button under the first — and both would be live.
         */
        host.current.replaceChildren();
        window.google.accounts.id.renderButton(host.current, {
          type: "standard",
          theme: document.documentElement.dataset.theme === "dark" ? "filled_black" : "outline",
          size: "large",
          text,
          shape: "rectangular",
          logo_alignment: "center",
          width,
        });
      })
      .catch(() => setBroken(true));
    return () => {
      cancelled = true;
    };
  }, [clientId, text, width]);

  if (broken) {
    /*
     * Said plainly rather than left as an empty space where a button should be.
     *
     * Google's script is blocked often enough to matter — corporate networks, tracker blockers, ad
     * blockers with aggressive lists. The password form is still on screen and still works, so this
     * points at it instead of implying the console is broken.
     */
    return (
      <p style={{ fontSize: "var(--text-meta)", color: "var(--muted)" }}>
        Google sign-in could not load — it may be blocked by your network or an extension. Use your
        email and password below.
      </p>
    );
  }

  return (
    <div
      ref={host}
      // `width: 100%` here is what the measurement above reads, so the rendered button matches the
      // form column. GIS renders an iframe-backed button that ignores pointer-events on its host, so
      // the disabled state is the visual half only — the handler is the half that counts.
      style={{ width: "100%", minHeight: 44, opacity: disabled ? 0.5 : 1 }}
      aria-busy={disabled ? "true" : undefined}
    />
  );
}
